import { formRegex, errorMessage } from '@helpers/Constants';
/**
 * A service for validating form fields and entire forms.
 *
 * This service includes two main functions:
 *
 * 1. `validateField`:
 *    - Validates a single field based on its type (e.g., "email", "newPassword", "confirmPassword").
 *    - Returns an error message if the field is invalid or an empty string if it's valid.
 *    - Specific validations include:
 *      - Email: Checks if the value matches the email format.
 *      - New Password: Ensures the password meets defined criteria (e.g., strength).
 *      - Confirm Password: Ensures it matches the "newPassword" field.
 *
 * 2. `handleValidation`:
 *    - Validates all fields in a form.
 *    - Iterates over the `formData` object, validates each field using `validateField`, and collects errors.
 *    - Returns an object with:
 *      - `isValid`: A boolean indicating if the entire form is valid.
 *      - `errors`: An object containing error messages for invalid fields.
 */
export const ValidationService = {
  validateField: (
    field: string,
    value: string,
    formData: Record<string, any>,
  ): string => {
    let isDuplicate: boolean;
    let price: number;

    switch (field) {
      case 'email':
        return formRegex.EMAIL.test(value) ? '' : errorMessage.INVALID_EMAIL;

      case 'newPassword':
        return formRegex.PASSWORD.test(value)
          ? ''
          : errorMessage.INVALID_PASSWORD;

      case 'confirmPassword':
        if (!value) {
          return errorMessage.FIELD_REQUIRED;
        }
        return value === formData.newPassword
          ? ''
          : errorMessage.PASSWORD_MATCH_FAILED;

      case 'name':
        if (!value.trim()) {
          return errorMessage.FIELD_REQUIRED;
        }
        if (formRegex.PRICE.test(value)) {
          return errorMessage.NAME_ERROR;
        }
        isDuplicate = formData.some(
          (addon: { name: string }) =>
            addon.name.toLowerCase() === value.toLowerCase().trim(),
        );
        return isDuplicate ? errorMessage.DUPLICATE_ERROR : '';

      case 'price':
        if (!value.trim()) {
          return errorMessage.FIELD_REQUIRED;
        }
        price = parseFloat(value);
        if (isNaN(price) || price <= 0) {
          return errorMessage.PRICE_NUMERIC_ERROR;
        }
        return '';

      case 'title':
      case 'units':
        if (!value.trim()) {
          return errorMessage.FIELD_REQUIRED;
        }
        return '';

      default:
        return '';
    }
  },
  handleValidation: (
    formData: Record<string, any>,
  ): { isValid: boolean; errors: { [key: string]: string } } => {
    const errors: { [key: string]: string } = {};

    Object.keys(formData).forEach((key) => {
      const value = formData[key];
      errors[key] = ValidationService.validateField(
        key,
        String(value),
        formData,
      );
    });

    const isValid = Object.values(errors).every((error) => !error);
    return { isValid, errors };
  },
};
